// Tenantra Inc. - Public document re-upload page. Opened from the email link when a screening
// is paused because a wrong, duplicate or unrecognized document was uploaded.
import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { AlertCircle, CheckCircle2, Loader2, MapPin, UploadCloud } from 'lucide-react';
import { Logo } from '@/components/Logo';
import api from '@/services/api';

const errText = (err) => { const d = err?.response?.data?.detail; return typeof d === 'string' ? d : Array.isArray(d) ? d.map(x => x?.msg).join(' ') : 'Something went wrong. Please try again.'; };

const DOC_TITLE = { paystub: 'Pay stub', bank_statement: 'Bank statement', savings_statement: 'Savings statement', equifax_report: 'Equifax credit report' };

function RejectedDoc({ token, item, onReady }) {
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  const [err, setErr] = useState('');
  const upload = async (file) => {
    if (!file) return;
    setBusy(true); setErr('');
    try {
      const fd = new FormData(); fd.append('file', file);
      const res = await api.post(`/screening/fix/${token}/document?doc_type=${item.doc_type}`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setDone(res.data.filename);
      onReady({ replace_file_id: item.file_id, file_id: res.data.file_id, filename: res.data.filename });
    } catch (e) { setErr(errText(e)); } finally { setBusy(false); }
  };
  return (
    <div className="bg-white border border-stone-300 rounded-md p-4" data-testid={`fix-item-${item.doc_type}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-[15px] font-semibold text-stone-900">{DOC_TITLE[item.doc_type] || item.doc_label}</p>
          <p className="text-sm text-stone-600 mt-0.5">{item.detail}</p>
          <p className="text-[11px] text-stone-500 mt-1 truncate">Replacing: {item.filename}</p>
        </div>
        {done && <CheckCircle2 className="w-5 h-5 text-teal-600 flex-shrink-0 mt-0.5" />}
      </div>
      <label className={`mt-3 inline-flex items-center gap-2 h-11 px-4 rounded-md border border-dashed text-sm cursor-pointer transition-colors ${busy ? 'opacity-60 pointer-events-none' : 'border-stone-300 text-stone-700 hover:border-stone-500 hover:bg-stone-50'}`} data-testid={`fix-upload-${item.doc_type}`}>
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <UploadCloud className="w-4 h-4" />}
        {busy ? 'Uploading…' : done ? 'Replace again' : 'Choose a file'}
        <input type="file" accept="application/pdf,image/*" className="hidden" disabled={busy} onChange={(e) => upload(e.target.files?.[0])} />
      </label>
      {done && <p className="text-[11px] text-stone-600 mt-2">Ready: {done}</p>}
      {err && <p className="text-[11px] text-red-600 mt-2">{err}</p>}
    </div>
  );
}

export default function FixPage() {
  const token = window.location.pathname.split('/').pop();
  const [info, setInfo] = useState(null);
  const [loadErr, setLoadErr] = useState('');
  const [reps, setReps] = useState({});
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const load = () => api.get(`/screening/fix/${token}`).then(r => { setInfo(r.data); setReps({}); }).catch(e => setLoadErr(errText(e)));
  useEffect(() => { load(); }, [token]); // eslint-disable-line

  const setReady = (rep) => setReps(x => ({ ...x, [rep.replace_file_id]: rep }));
  const allReady = info && info.rejected.every(r => reps[r.file_id]);

  const submit = async () => {
    setBusy(true); setError('');
    try {
      const res = await api.post(`/screening/fix/${token}`, { replacements: Object.values(reps) });
      if (res.data.resolved) { setDone(true); window.scrollTo(0, 0); }
      else { setInfo(i => ({ ...i, rejected: res.data.rejected })); setReps({}); setError('Some documents still need attention. Please upload the correct files below.'); }
    } catch (e) { setError(errText(e)); } finally { setBusy(false); }
  };

  const shell = (children) => (
    <div className="apply-type min-h-screen bg-white">
      <header className="bg-white/90 backdrop-blur border-b border-stone-200 sticky top-0 z-20">
        <div className="max-w-2xl mx-auto px-5 h-14 flex items-center"><Logo /></div>
      </header>
      <main className="max-w-2xl mx-auto px-5 py-10 sm:py-14">{children}</main>
    </div>
  );

  if (loadErr) return shell(<div className="bg-white border border-stone-200 rounded-2xl shadow-sm p-8 text-center" data-testid="fix-link-error"><AlertCircle className="w-6 h-6 text-stone-400 mx-auto mb-2" /><p className="text-sm text-stone-700">{loadErr}</p></div>);
  if (!info) return shell(<div className="py-24 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-stone-400" /></div>);
  if (done) return shell(
    <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="bg-white border border-stone-200 rounded-2xl shadow-sm p-10 text-center" data-testid="fix-success">
      <div className="w-14 h-14 rounded-full bg-teal-50 flex items-center justify-center mx-auto mb-4"><CheckCircle2 className="w-7 h-7 text-teal-600" /></div>
      <h1 className="text-2xl font-bold tracking-tight text-stone-900">Documents received</h1>
      <p className="text-[15px] leading-relaxed text-stone-600 mt-4 max-w-md mx-auto">Thank you. Your documents passed our checks and your application has resumed. The property manager will continue your review.</p>
    </motion.div>
  );
  const p = info.property;
  return shell(
    <div data-testid="fix-page">
      <p className="text-sm text-stone-800 flex items-center gap-1.5 mb-3"><MapPin className="w-4 h-4 text-stone-700" /> {p.street}, {p.city}, {p.province}</p>
      <h1 className="text-[1.75rem] font-bold tracking-tight text-stone-900 leading-tight">Re-upload your documents</h1>
      <p className="text-base text-stone-900 mt-2 mb-6">Hi {info.applicant_name}, a few documents on application {info.reference} need to be corrected before we can finish your screening. Upload the right file for each item below.</p>
      <div className="space-y-4">
        {info.rejected.map((r, i) => <RejectedDoc key={r.file_id + i} token={token} item={r} onReady={setReady} />)}
      </div>
      {error && <div className="mt-6 p-3 bg-red-50 border border-red-200 rounded-md flex items-center gap-2 text-red-600 text-sm" data-testid="fix-error"><AlertCircle className="w-4 h-4 flex-shrink-0" />{error}</div>}
      <button type="button" onClick={submit} disabled={busy || !allReady} className="mt-8 w-full sm:w-auto justify-center h-12 px-7 rounded-xl active:scale-[0.98] transition-all bg-teal-600 hover:bg-teal-700 text-white text-[15px] font-semibold inline-flex items-center gap-2 disabled:opacity-50 shadow-md shadow-teal-600/10" data-testid="fix-submit">
        {busy && <Loader2 className="w-4 h-4 animate-spin" />}{busy ? 'Submitting…' : 'Submit documents'}
      </button>
      {!allReady && <p className="text-[12px] text-stone-500 mt-3">Upload a file for each document to continue.</p>}
    </div>
  );
}
